import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getGmailStatus,
  getGmailLabels,
  getGmailMessages,
  getGmailMessage,
  markGmailMessageAsRead,
  sendGmailMessage,
  getGmailSendAs,
  getGmailOAuthUrl,
  exportGmailAddressesToExcel,
  type GmailStatus,
  type GmailLabel,
  type GmailMessageListItem,
  type GmailMessageFull,
  type SendAsItem,
} from '../services/gmail';
import { EnvelopeIcon, RefreshIcon, XIcon } from './icons/AdminIcons';
import './GmailSection.css';

/** Decodifica base64 a string UTF-8 para que tildes y ñ se vean bien (evita Ã¡ en lugar de á). */
function base64ToUtf8(base64: string): string {
  try {
    const binary = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return '';
  }
}

function getHeader(msg: GmailMessageFull | GmailMessageListItem, name: string): string {
  const headers = msg.payload?.headers;
  if (!headers) return '';
  const h = headers.find((x) => x.name.toLowerCase() === name.toLowerCase());
  return h?.value || '';
}

function formatDate(internalDate?: string): string {
  if (!internalDate) return '';
  const d = new Date(parseInt(internalDate, 10));
  return isNaN(d.getTime()) ? '' : d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}

interface GmailSectionProps {
  isDarkMode?: boolean;
  /** Si se indica, solo se muestran correos donde este email participa (from o to). Oculta etiquetas. */
  filterByEmail?: string | null;
}

const GmailSection: React.FC<GmailSectionProps> = ({ isDarkMode, filterByEmail }) => {
  const [status, setStatus] = useState<GmailStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [labels, setLabels] = useState<GmailLabel[]>([]);
  const [selectedLabelId, setSelectedLabelId] = useState<string>('INBOX');
  const [messages, setMessages] = useState<GmailMessageListItem[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTokens, setPageTokens] = useState<(string | undefined)[]>([undefined]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messageDetail, setMessageDetail] = useState<GmailMessageFull | null>(null);
  const [sendAsList, setSendAsList] = useState<SendAsItem[]>([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendForm, setSendForm] = useState({ to: '', subject: '', body: '' });
  const [sendAs, setSendAs] = useState<string>('');
  const [listLoading, setListLoading] = useState(false);
  const filterQuery = filterByEmail?.trim() ? `from:${filterByEmail.trim()} OR to:${filterByEmail.trim()}` : undefined;
  const scrollToIdRef = useRef<string | null>(null);

  const [exportDateFrom, setExportDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [exportDateTo, setExportDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [exportAllFolders, setExportAllFolders] = useState(true);
  const [exportLabelIds, setExportLabelIds] = useState<string[]>([]);
  const [exportMaxMessages, setExportMaxMessages] = useState(5000);
  const [exportingAddresses, setExportingAddresses] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const data = await getGmailStatus();
      setStatus({ connected: data.connected, email: data.email });
      setError(null);
      return data.connected;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al conectar');
      setStatus({ connected: false, email: null });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const PAGE_SIZE = 50;

  const loadMessages = useCallback(async (pageToken?: string, labelId?: string, q?: string): Promise<{ messages: GmailMessageListItem[]; nextPageToken: string | null } | undefined> => {
    const lid = labelId ?? selectedLabelId;
    const queryToUse = q ?? filterQuery;
    setListLoading(true);
    try {
      const data = await getGmailMessages({
        maxResults: PAGE_SIZE,
        pageToken,
        labelId: queryToUse ? undefined : (lid || undefined),
        q: queryToUse,
      });
      setMessages(data.messages || []);
      setNextPageToken(data.nextPageToken || null);
      return { messages: data.messages || [], nextPageToken: data.nextPageToken || null };
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar mensajes');
      return undefined;
    } finally {
      setListLoading(false);
    }
  }, [selectedLabelId, filterQuery]);

  const loadSendAs = useCallback(async () => {
    try {
      const data = await getGmailSendAs();
      setSendAsList(data.sendAs || []);
      const primary = data.sendAs?.find((s) => s.isPrimary);
      if (primary) setSendAs(primary.sendAsEmail);
    } catch (_) {
      setSendAsList([]);
    }
  }, []);

  const loadLabels = useCallback(async () => {
    try {
      const data = await getGmailLabels();
      setLabels(data.labels || []);
    } catch (_) {
      setLabels([]);
    }
  }, []);

  const SYSTEM_LABEL_ORDER: Record<string, number> = {
    INBOX: 0,
    STARRED: 1,
    SNOOZED: 2,
    SENT: 3,
    DRAFT: 4,
    SPAM: 5,
    TRASH: 6,
    IMPORTANT: 7,
    UNREAD: 8,
    CATEGORY_PERSONAL: 9,
    CATEGORY_SOCIAL: 10,
    CATEGORY_PROMOTIONS: 11,
    CATEGORY_UPDATES: 12,
    CATEGORY_FORUMS: 13,
  };
  const sortedLabels = React.useMemo(() => {
    const visible = labels.filter((l) => l.labelListVisibility !== 'labelHide');
    const system: GmailLabel[] = [];
    const user: GmailLabel[] = [];
    visible.forEach((l) => {
      if (l.type === 'system') system.push(l);
      else user.push(l);
    });
    system.sort((a, b) => {
      const orderA = SYSTEM_LABEL_ORDER[a.id] ?? 999;
      const orderB = SYSTEM_LABEL_ORDER[b.id] ?? 999;
      return orderA - orderB;
    });
    user.sort((a, b) => a.name.localeCompare(b.name, 'es'));
    return [...system, ...user];
  }, [labels]);

  const getLabelDepth = (name: string) => (name.match(/\//g) || []).length;

  useEffect(() => {
    loadStatus().then((connected) => {
      if (connected) {
        loadLabels();
        loadSendAs();
      }
    });
  }, [loadStatus, loadSendAs, loadLabels]);

  // Cargar mensajes al conectar y al cambiar de etiqueta o filtro por email
  useEffect(() => {
    if (!status?.connected) return;
    setSelectedId(null);
    setCurrentPage(1);
    setPageTokens([undefined]);
    loadMessages(undefined, selectedLabelId, filterQuery).then((data) => {
      if (data) setPageTokens((prev) => { const n = [...prev]; n[1] = data.nextPageToken ?? undefined; return n; });
    });
  }, [status?.connected, selectedLabelId, filterQuery]);

  useEffect(() => {
    if (!selectedId) {
      setMessageDetail(null);
      return;
    }
    getGmailMessage(selectedId)
      .then((data) => {
        setMessageDetail(data.message);
        const msg = data.message;
        if (msg?.labelIds?.includes('UNREAD')) {
          markGmailMessageAsRead(selectedId)
            .then(() => {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === selectedId ? { ...m, labelIds: (m.labelIds || []).filter((l) => l !== 'UNREAD') } : m
                )
              );
            })
            .catch(() => {});
        }
      })
      .catch(() => setMessageDetail(null));
  }, [selectedId]);

  // Al volver de detalle, scroll a la posición del correo que se estaba viendo
  useEffect(() => {
    if (selectedId !== null || !scrollToIdRef.current) return;
    const id = scrollToIdRef.current;
    scrollToIdRef.current = null;
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-gmail-message-id="${id}"]`);
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }, [selectedId]);

  const handleConnect = () => {
    window.location.href = getGmailOAuthUrl();
  };

  const handleSwitchAccount = () => {
    const current = status?.email ? ` (${status.email})` : '';
    const ok = window.confirm(
      `Esto reemplaza la cuenta de correo de todo el panel${current}. Vas a poder elegir otra cuenta de Google.`
    );
    if (!ok) return;
    window.location.href = getGmailOAuthUrl();
  };

  const handleExportAddresses = async () => {
    if (!exportAllFolders && exportLabelIds.length === 0) {
      setError('Seleccioná al menos una carpeta o marcá «Todas las carpetas».');
      return;
    }
    setExportingAddresses(true);
    setError(null);
    try {
      const blob = await exportGmailAddressesToExcel({
        dateFrom: exportDateFrom,
        dateTo: exportDateTo,
        allFolders: exportAllFolders,
        labelIds: exportAllFolders ? [] : exportLabelIds,
        maxMessages: exportMaxMessages,
        extraQuery: filterByEmail?.trim()
          ? `from:${filterByEmail.trim()} OR to:${filterByEmail.trim()}`
          : undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `direcciones-correo_${exportDateFrom}_${exportDateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al exportar direcciones');
    } finally {
      setExportingAddresses(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    loadStatus().then((connected) => {
      if (connected) {
        loadLabels();
        setCurrentPage(1);
        setPageTokens([undefined]);
        loadMessages(undefined, selectedLabelId, filterQuery).then((data) => {
          if (data) setPageTokens((prev) => { const n = [...prev]; n[1] = data.nextPageToken ?? undefined; return n; });
        });
        loadSendAs();
      }
    });
  };

  const handlePaginaAnterior = () => {
    if (currentPage <= 1) return;
    const prevPage = currentPage - 1;
    setCurrentPage(prevPage);
    const token = prevPage === 1 ? undefined : pageTokens[prevPage - 1];
    loadMessages(token, selectedLabelId, filterQuery);
  };

  const handlePaginaSiguiente = () => {
    if (!nextPageToken) return;
    const token = pageTokens[currentPage];
    loadMessages(token, selectedLabelId, filterQuery).then((data) => {
      if (data) {
        setCurrentPage((p) => p + 1);
        setPageTokens((prev) => { const n = [...prev]; n[currentPage + 1] = data.nextPageToken ?? undefined; return n; });
      }
    });
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sendForm.to.trim() || !sendForm.subject.trim()) return;
    setSending(true);
    try {
      await sendGmailMessage({
        to: sendForm.to.trim(),
        subject: sendForm.subject.trim(),
        body: sendForm.body || undefined,
        sendAs: sendAs || undefined,
      });
      setSendForm({ to: '', subject: '', body: '' });
      setComposeOpen(false);
      loadMessages(undefined, selectedLabelId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al enviar');
    } finally {
      setSending(false);
    }
  };

  /** Busca recursivamente la primera parte con cuerpo text/html o text/plain (para multipart anidados). */
  const findBodyPart = (payload: GmailMessageFull['payload']): string => {
    if (!payload) return '';
    const parts = payload.parts || [];
    const htmlPart = parts.find((p) => p.mimeType === 'text/html' && p.body?.data);
    const textPart = parts.find((p) => p.mimeType === 'text/plain' && p.body?.data);
    if (htmlPart?.body?.data) return base64ToUtf8(htmlPart.body.data);
    if (textPart?.body?.data) return base64ToUtf8(textPart.body.data);
    for (const p of parts) {
      if (p.mimeType?.startsWith('multipart/') && (p as { parts?: typeof parts }).parts?.length) {
        const nested = findBodyPart(p as unknown as GmailMessageFull['payload']);
        if (nested) return nested;
      }
    }
    if (payload.body?.data) return base64ToUtf8(payload.body.data);
    return '';
  };

  const decodeBody = (msg: GmailMessageFull): string => {
    const body = findBodyPart(msg.payload);
    if (body) return body;
    return msg.snippet || '';
  };

  if (loading) {
    return (
      <div className="gmail-section gmail-section-loading">
        <div className="gmail-spinner" />
        <p>Cargando...</p>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="gmail-section gmail-section-connect">
        <div className="gmail-connect-card">
          <EnvelopeIcon width={48} height={48} className="gmail-connect-icon" />
          <h2>Conectar Gmail</h2>
          <p>Conecta tu cuenta de Google Workspace para ver y enviar correo desde el panel.</p>
          {error && <p className="gmail-error">{error}</p>}
          <button type="button" className="gmail-btn gmail-btn-primary" onClick={handleConnect}>
            Conectar con Google
          </button>
        </div>
      </div>
    );
  }

  const showLabelsSidebar = !filterQuery;

  return (
    <div className={`gmail-section ${isDarkMode ? 'gmail-section-dark' : ''}`}>
      <div className="gmail-header">
        <div>
          <h2><EnvelopeIcon width={24} height={24} /> Correo</h2>
          <p className="gmail-email">
            {filterQuery ? `Correos con ${filterByEmail}` : (status.email || 'Cuenta conectada')}
          </p>
        </div>
        <div className="gmail-header-actions">
          <button type="button" className="gmail-btn gmail-btn-icon" onClick={handleRefresh} title="Actualizar">
            <RefreshIcon width={18} height={18} />
          </button>
          {!filterByEmail && (
            <button
              type="button"
              className="gmail-btn"
              onClick={handleSwitchAccount}
              title="Conectar otra cuenta de Google"
            >
              Cambiar cuenta
            </button>
          )}
          <button type="button" className="gmail-btn gmail-btn-primary" onClick={() => setComposeOpen(true)}>
            Redactar
          </button>
        </div>
      </div>

      <div className="gmail-export-panel">
        <h3 className="gmail-export-title">Exportar direcciones y conversaciones a Excel</h3>
        <p className="gmail-export-hint">
          La hoja <strong>Direcciones</strong> lista los correos de las cabeceras (remitente, para, copia, etc.).
          La hoja <strong>Conversaciones</strong> tiene, para cada uno de esos correos, el asunto y el hilo de
          mensajes del rango. Los rangos muy amplios pueden tardar varios minutos.
        </p>
        {filterQuery && (
          <p className="gmail-export-filter-note">
            Filtro activo: solo mensajes donde participa <strong>{filterByEmail}</strong>.
          </p>
        )}
        <div className="gmail-export-row">
          <label className="gmail-export-label">
            Desde
            <input
              type="date"
              value={exportDateFrom}
              onChange={(e) => setExportDateFrom(e.target.value)}
              disabled={exportingAddresses}
            />
          </label>
          <label className="gmail-export-label">
            Hasta
            <input
              type="date"
              value={exportDateTo}
              onChange={(e) => setExportDateTo(e.target.value)}
              disabled={exportingAddresses}
            />
          </label>
          <label className="gmail-export-label gmail-export-max">
            Máx. mensajes
            <input
              type="number"
              min={100}
              max={20000}
              step={100}
              value={exportMaxMessages}
              onChange={(e) => setExportMaxMessages(parseInt(e.target.value, 10) || 5000)}
              disabled={exportingAddresses}
              title="Límite de seguridad para no exceder cuota de Gmail (máx. 20.000)"
            />
          </label>
        </div>
        <label className="gmail-export-check">
          <input
            type="checkbox"
            checked={exportAllFolders}
            onChange={(e) => {
              setExportAllFolders(e.target.checked);
              if (e.target.checked) setExportLabelIds([]);
            }}
            disabled={exportingAddresses}
          />
          Todas las carpetas / etiquetas
        </label>
        {!exportAllFolders && (
          <div className="gmail-export-folders">
            <span className="gmail-export-label-text">Carpetas (Ctrl/Cmd + clic para varias):</span>
            <select
              multiple
              className="gmail-export-select-multi"
              value={exportLabelIds}
              onChange={(e) => {
                const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
                setExportLabelIds(opts);
              }}
              disabled={exportingAddresses}
              size={Math.min(10, Math.max(4, sortedLabels.length))}
            >
              {sortedLabels.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <button
          type="button"
          className="gmail-btn gmail-btn-primary gmail-export-btn"
          onClick={handleExportAddresses}
          disabled={exportingAddresses || !exportDateFrom || !exportDateTo}
        >
          {exportingAddresses ? 'Generando Excel…' : 'Descargar Excel (.xlsx)'}
        </button>
      </div>

      {error && (
        <div className="gmail-toast gmail-toast-error">
          {error}
          <button type="button" aria-label="Cerrar" onClick={() => setError(null)}><XIcon width={16} height={16} /></button>
        </div>
      )}

      <div className={`gmail-layout ${filterQuery ? 'gmail-layout-no-labels' : ''}`}>
        {showLabelsSidebar && (
          <div className="gmail-labels-sidebar">
            <div className="gmail-labels-header">Etiquetas</div>
            <ul className="gmail-labels-list">
              {sortedLabels.map((label) => {
                const depth = getLabelDepth(label.name);
                const displayName = depth > 0 ? label.name.split('/').pop()! : label.name;
                return (
                  <li key={label.id} className="gmail-label-item" data-depth={depth}>
                    <button
                      type="button"
                      className={`gmail-label-btn ${selectedLabelId === label.id ? 'active' : ''}`}
                      onClick={() => setSelectedLabelId(label.id)}
                      style={{ paddingLeft: `${12 + depth * 16}px` }}
                      title={label.name}
                    >
                      <span className="gmail-label-name">{displayName}</span>
                      {typeof label.messagesUnread === 'number' && label.messagesUnread > 0 && (
                        <span className="gmail-label-unread">{label.messagesUnread}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="gmail-main">
          {selectedId && messageDetail ? (
            <>
              <div className="gmail-detail-toolbar">
                <button
                type="button"
                className="gmail-btn gmail-btn-back"
                onClick={() => {
                  scrollToIdRef.current = selectedId;
                  setSelectedId(null);
                }}
                aria-label="Volver a la lista"
              >
                  ← Volver
                </button>
              </div>
              <div className="gmail-detail">
                <div className="gmail-detail-header">
                  <h3>{getHeader(messageDetail, 'Subject') || '(Sin asunto)'}</h3>
                  <p className="gmail-detail-meta">
                    De: {getHeader(messageDetail, 'From')} · {formatDate(messageDetail.internalDate)}
                  </p>
                </div>
                <div
                  className="gmail-detail-body"
                  dangerouslySetInnerHTML={{ __html: decodeBody(messageDetail) || messageDetail.snippet || '' }}
                />
              </div>
            </>
          ) : (
            <>
              <div className="gmail-list-toolbar">
                <span>{filterQuery ? `Conversación con ${filterByEmail}` : (labels.find((l) => l.id === selectedLabelId)?.name ?? selectedLabelId)}</span>
                {listLoading && <span className="gmail-loading-dots">Cargando...</span>}
              </div>
              <ul className="gmail-message-list">
                {messages.map((m) => {
                  const isUnread = Array.isArray(m.labelIds) && m.labelIds.includes('UNREAD');
                  return (
                  <li
                    key={m.id}
                    data-gmail-message-id={m.id}
                    className={`gmail-message-item ${selectedId === m.id ? 'selected' : ''} ${isUnread ? 'unread' : ''}`}
                    onClick={() => setSelectedId(m.id)}
                    title={`${getHeader(m, 'From') || '(Sin remitente)'}\n${getHeader(m, 'Subject') || '(Sin asunto)'}\n${(m.snippet || '').slice(0, 150)}`}
                  >
                    <div className="gmail-message-item-from">{getHeader(m, 'From') || '(Sin remitente)'}</div>
                    <div className="gmail-message-item-subject">{getHeader(m, 'Subject') || '(Sin asunto)'}</div>
                    <div className="gmail-message-item-snippet">{m.snippet || ''}</div>
                    <div className="gmail-message-item-date">{formatDate(m.internalDate)}</div>
                  </li>
                  );
                })}
              </ul>
              <div className="gmail-pagination">
                <button
                  type="button"
                  className="gmail-btn gmail-btn-pag"
                  onClick={handlePaginaAnterior}
                  disabled={listLoading || currentPage <= 1}
                >
                  ← Anterior
                </button>
                <span className="gmail-pagination-page">Página {currentPage}</span>
                <button
                  type="button"
                  className="gmail-btn gmail-btn-pag"
                  onClick={handlePaginaSiguiente}
                  disabled={listLoading || !nextPageToken}
                >
                  Siguiente →
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {composeOpen && (
        <div className="gmail-modal-overlay" onClick={() => !sending && setComposeOpen(false)}>
          <div className="gmail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gmail-modal-header">
              <h3>Nuevo mensaje</h3>
              <button type="button" aria-label="Cerrar" onClick={() => !sending && setComposeOpen(false)}><XIcon width={20} height={20} /></button>
            </div>
            <form onSubmit={handleSend} className="gmail-compose-form">
              <div className="gmail-form-row">
                <label>Para</label>
                <input
                  type="email"
                  value={sendForm.to}
                  onChange={(e) => setSendForm((f) => ({ ...f, to: e.target.value }))}
                  placeholder="correo@ejemplo.com"
                  required
                />
              </div>
              <div className="gmail-form-row">
                <label>Asunto</label>
                <input
                  type="text"
                  value={sendForm.subject}
                  onChange={(e) => setSendForm((f) => ({ ...f, subject: e.target.value }))}
                  placeholder="Asunto"
                  required
                />
              </div>
              {sendAsList.length > 1 && (
                <div className="gmail-form-row">
                  <label>Enviar como</label>
                  <select value={sendAs} onChange={(e) => setSendAs(e.target.value)}>
                    {sendAsList.map((s) => (
                      <option key={s.sendAsEmail} value={s.sendAsEmail}>
                        {s.displayName || s.sendAsEmail}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="gmail-form-row">
                <label>Mensaje</label>
                <textarea
                  value={sendForm.body}
                  onChange={(e) => setSendForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder="Escribe tu mensaje..."
                  rows={8}
                />
              </div>
              <div className="gmail-modal-actions">
                <button type="button" className="gmail-btn" onClick={() => !sending && setComposeOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="gmail-btn gmail-btn-primary" disabled={sending}>
                  {sending ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default GmailSection;
